import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useDining from "../../services/Order/useDining";
import { RotateCcw } from "lucide-react";
import LoadingScreen from "../../utils/LoadingScreen";

const Dining = () => {
  const navigate = useNavigate();

  const {
    tables,
    rooms,
    loading,
    fetchTables,
    addTable,
    deleteTable,
    fetchRooms,
    addRoom,
  } = useDining();

  const [activeTab, setActiveTab] = useState("table");
  const [showModal, setShowModal] = useState(false);
  const [tableNumber, setTableNumber] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [addingTable, setAddingTable] = useState(false);
  const [addingRoom, setAddingRoom] = useState(false);

  const loadTables = async (force = false) => {
    await fetchTables({ force });
  };

  const loadRooms = async (force = false) => {
    await fetchRooms({ force });
  };

  const loadActiveTabData = async (force = false) => {
    if (activeTab === "table") {
      await loadTables(force);
    } else {
      await loadRooms(force);
    }
  };

  useEffect(() => {
    loadActiveTabData();
  }, [activeTab]);

  const handleAddTable = async () => {
    if (!tableNumber.trim()) return;

    try {
      setAddingTable(true);

      const res = await addTable(tableNumber.trim());

      if (res) {
        setTableNumber("");
        setShowModal(false);
      }
    } catch (error) {
      console.error("ADD TABLE ERROR:", error);
    } finally {
      setAddingTable(false);
    }
  };

  const handleAddRoom = async () => {
    if (!roomNumber.trim()) return;

    try {
      setAddingRoom(true);

      const res = await addRoom(roomNumber.trim());

      if (res) {
        setRoomNumber("");
        setShowModal(false);
      }
    } catch (error) {
      console.error("ADD ROOM ERROR:", error);
    } finally {
      setAddingRoom(false);
    }
  };

  const handleOpenAddModal = () => {
    setTableNumber("");
    setRoomNumber("");
    setShowModal(true);
  };

  const handleSubmit = () => {
    if (activeTab === "table") {
      handleAddTable();
    } else {
      handleAddRoom();
    }
  };

  const isSubmitDisabled =
    activeTab === "table"
      ? !tableNumber.trim() || addingTable
      : !roomNumber.trim() || addingRoom;

  return (
    <div className="h-full flex flex-col min-h-0 montserrat-font-1">
      <div className="sticky top-0 z-30 bg-white px-6 pt-6">
        <div className="flex justify-between items-center mb-6 montserrat-font-1">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">
              Dining Order Management
            </h1>

            <p className="text-sm text-gray-500 mt-1">
              Manage restaurant tables and room order rooms.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleOpenAddModal}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow"
            >
              {activeTab === "table" ? "Add New Table" : "Add New Room"}
            </button>

            {activeTab === "table" && (
              <button
                onClick={() => navigate("/Orders/QR_Code")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
              >
                Download QR Codes
              </button>
            )}

            <button
              type="button"
              onClick={() => loadActiveTabData(true)}
              disabled={loading}
              className="flex items-center gap-2 border px-3 py-2 rounded-lg text-white bg-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className={loading ? "animate-spin" : ""}>
                <RotateCcw className="w-4 h-4" />
              </span>

              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-3 mb-6 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab("table")}
            className={`px-5 py-3 font-semibold border-b-4 transition-all ${
              activeTab === "table"
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-green-600"
            }`}
          >
            Tables
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("room")}
            className={`px-5 py-3 font-semibold border-b-4 transition-all ${
              activeTab === "room"
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-green-600"
            }`}
          >
            Rooms
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-6 pb-24">
          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-10 montserrat-font-1">
              <LoadingScreen text="Loading..." />
            </div>
          ) : activeTab === "table" ? (
            <div>
              {tables.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border">
                  <h3 className="text-lg font-semibold text-gray-700">
                    No tables added.
                  </h3>
                  <p className="text-gray-500 mt-1">
                    Click Add New Table to create a table.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {tables.map((table) => (
                    <div
                      key={table.id}
                      className={`p-6 rounded-xl shadow-md border-l-4 border transition-all duration-300 cursor-pointer bg-white ${
                        table.status !== "Open"
                          ? "hover:bg-red-50 border-red-200 border-l-red-500 hover:shadow-lg hover:-translate-y-1"
                          : "hover:bg-green-50 border-green-200 border-l-green-500 hover:shadow-lg hover:-translate-y-1"
                      }`}
                    >
                      <h3 className="text-xl font-semibold mb-2">
                        Table {table.tableNo}
                      </h3>

                      <p className="text-gray-700">
                        Status:{" "}
                        <span
                          className={`font-semibold ${
                            table.status !== "Open"
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {table.status !== "Open" ? "Booked" : "Available"}
                        </span>
                      </p>

                      <p className="text-gray-700 mt-1">
                        Reservation:{" "}
                        <span
                          className={`font-semibold ${
                            table.reservationStatus === "Y"
                              ? "text-orange-600"
                              : "text-green-600"
                          }`}
                        >
                          {table.reservationStatus === "Y"
                            ? "Reserved"
                            : "Free"}
                        </span>
                      </p>

                      {table.orderId && (
                        <p className="text-gray-500 mt-1 text-sm">
                          Order ID: {table.orderId}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              {rooms.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border">
                  <h3 className="text-lg font-semibold text-gray-700">
                    No rooms added.
                  </h3>
                  <p className="text-gray-500 mt-1">
                    Click Add New Room to create a room.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {rooms.map((room) => {
                    const isAvailable =
                      room.status === "Open" && room.reservationStatus === "N";

                    return (
                      <div
                        key={room.id}
                        className={`p-6 rounded-xl shadow-md border-l-4 border transition-all duration-300 cursor-pointer bg-white ${
                          isAvailable
                            ? "hover:bg-green-50 border-green-200 border-l-green-500 hover:shadow-lg hover:-translate-y-1"
                            : "hover:bg-orange-50 border-orange-200 border-l-orange-500 hover:shadow-lg hover:-translate-y-1"
                        }`}
                      >
                        <h3 className="text-xl font-semibold mb-2">
                          Room {room.roomNumber}
                        </h3>

                        <p className="text-gray-700">
                          Status:{" "}
                          <span
                            className={`font-semibold ${
                              isAvailable ? "text-green-600" : "text-orange-600"
                            }`}
                          >
                            {isAvailable ? "Available" : "Ongoing"}
                          </span>
                        </p>

                        <p className="text-gray-700 mt-1">
                          Reservation:{" "}
                          <span
                            className={`font-semibold ${
                              room.reservationStatus === "Y"
                                ? "text-orange-600"
                                : "text-green-600"
                            }`}
                          >
                            {room.reservationStatus === "Y"
                              ? "Reserved"
                              : "Free"}
                          </span>
                        </p>

                        {room.orderId && (
                          <p className="text-gray-500 mt-1 text-sm">
                            Order ID: {room.orderId}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center montserrat-font-1 bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl w-96 p-6 montserrat-font-1">
            <h2 className="text-lg font-semibold mb-4">
              {activeTab === "table" ? "Add New Table" : "Add New Room"}
            </h2>

            <input
              type="text"
              placeholder={
                activeTab === "table"
                  ? "Enter table number"
                  : "Enter room number"
              }
              value={activeTab === "table" ? tableNumber : roomNumber}
              onChange={(e) => {
                if (activeTab === "table") {
                  setTableNumber(e.target.value);
                } else {
                  setRoomNumber(e.target.value);
                }
              }}
              className="w-full border rounded-lg p-2 mb-4 focus:ring focus:ring-blue-200"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setTableNumber("");
                  setRoomNumber("");
                  setShowModal(false);
                }}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {activeTab === "table"
                  ? addingTable
                    ? "Adding..."
                    : "Add Table"
                  : addingRoom
                    ? "Adding..."
                    : "Add Room"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dining;
